// src/services/conversationService.js
// Couche service — opérations Firebase pour la messagerie

import { db, storage } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const conversationService = {
  // ── Conversations ───────────────────────────────────────

  /**
   * Récupère une conversation par son ID.
   * Retourne l'objet conversation ou null si inexistante.
   */
  async getConversation(conversationId) {
    try {
      const snap = await getDoc(doc(db, 'conversations', conversationId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (err) {
      console.error('conversationService.getConversation error:', err);
      return null;
    }
  },

  /**
   * Crée ou retourne une conversation existante entre deux users.
   * ID déterministe pour les matchs : [uid1, uid2].sort().join('_')
   */
  async createConversation(currentUid, otherUid, type = 'match', eventId = null) {
    try {
      if (type === 'match') {
        // ID déterministe — même convention que la Cloud Function
        const convId = [currentUid, otherUid].sort().join('_');
        const convRef = doc(db, 'conversations', convId);
        const convSnap = await getDoc(convRef);

        if (convSnap.exists()) {
          return { success: true, conversation: { id: convId, ...convSnap.data() } };
        }

        const convData = {
          participants: [currentUid, otherUid].sort(),
          type: 'match',
          eventId: null,
          lastMessage: '',
          lastMessageAt: serverTimestamp(),
          lastMessageSenderId: null,
          readBy: {},
          createdAt: serverTimestamp(),
        };
        await setDoc(convRef, convData);
        return { success: true, conversation: { id: convId, ...convData } };
      }

      // Type "event" — vérifier qu'une conversation n'existe pas déjà
      const existing = await this.findExistingConversation(currentUid, otherUid, 'event', eventId);
      if (existing) {
        return { success: true, conversation: existing };
      }

      const convData = {
        participants: [currentUid, otherUid].sort(),
        type: 'event',
        eventId: eventId || null,
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        lastMessageSenderId: null,
        readBy: {},
        createdAt: serverTimestamp(),
      };
      const convRef = await addDoc(collection(db, 'conversations'), convData);
      return { success: true, conversation: { id: convRef.id, ...convData } };
    } catch (err) {
      console.error('conversationService.createConversation error:', err);
      return { success: false, error: 'Erreur lors de la création de la conversation' };
    }
  },

  /**
   * Cherche une conversation existante entre deux users.
   */
  async findExistingConversation(uid1, uid2, type = 'match', eventId = null) {
    try {
      const q = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', uid1),
        where('type', '==', type)
      );
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const data = d.data();
        if (data.participants.includes(uid2)) {
          if (type === 'event' && eventId && data.eventId !== eventId) continue;
          return { id: d.id, ...data };
        }
      }
      return null;
    } catch (err) {
      console.error('conversationService.findExistingConversation error:', err);
      return null;
    }
  },

  /**
   * Abonnement temps réel aux conversations d'un user.
   * Retourne la fonction unsubscribe.
   */
  subscribeToConversations(uid, callback) {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const conversations = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        const unreadCount = conversations.filter((c) => {
          if (!c.lastMessageSenderId || c.lastMessageSenderId === uid) return false;
          const userReadAt = c.readBy?.[uid];
          if (!userReadAt) return true;
          const lastMsgTime = c.lastMessageAt?.toMillis?.() || 0;
          const readTime = userReadAt?.toMillis?.() || 0;
          return lastMsgTime > readTime;
        }).length;
        callback({ conversations, unreadCount });
      },
      (err) => console.error('subscribeToConversations onSnapshot error:', err)
    );
  },

  /**
   * Abonnement temps réel à un document conversation (readBy, lastMessage, etc.).
   */
  subscribeToConversation(conversationId, callback) {
    return onSnapshot(
      doc(db, 'conversations', conversationId),
      (snap) => {
        if (snap.exists()) {
          callback({ id: snap.id, ...snap.data() });
        }
      },
      (err) => console.error('subscribeToConversation onSnapshot error:', err)
    );
  },

  // ── Messages ────────────────────────────────────────────

  /**
   * Envoie un message dans une conversation.
   * Met à jour lastMessage/lastMessageAt/lastMessageSenderId sur la conversation.
   */
  async sendMessage(conversationId, senderId, text, imageUrl = null) {
    try {
      const msgData = {
        senderId,
        text: text || '',
        imageUrl: imageUrl || null,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), msgData);

      // Mettre à jour la conversation
      await updateDoc(doc(db, 'conversations', conversationId), {
        lastMessage: imageUrl ? '📷 Photo' : (text || '').slice(0, 50),
        lastMessageAt: serverTimestamp(),
        lastMessageSenderId: senderId,
      });

      return { success: true };
    } catch (err) {
      console.error('conversationService.sendMessage error:', err);
      return { success: false, error: "Erreur lors de l'envoi du message" };
    }
  },

  /**
   * Abonnement temps réel aux messages d'une conversation.
   * Charge les `messageLimit` derniers messages.
   */
  subscribeToMessages(conversationId, callback, messageLimit = 30) {
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('createdAt', 'desc'),
      firestoreLimit(messageLimit)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .reverse();
        callback(messages);
      },
      (err) => console.error('subscribeToMessages onSnapshot error:', err)
    );
  },

  // ── Read status ─────────────────────────────────────────

  /**
   * Marque une conversation comme lue pour un user.
   */
  async markAsRead(conversationId, userId) {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`readBy.${userId}`]: serverTimestamp(),
      });
      return { success: true };
    } catch (err) {
      console.error('conversationService.markAsRead error:', err);
      return { success: false };
    }
  },

  // ── Upload image chat ───────────────────────────────────

  /**
   * Upload une image pour le chat (resize 800px max, JPEG 0.7).
   */
  async uploadChatImage(userId, file) {
    try {
      const blob = await new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          let w = img.width,
            h = img.height;
          const max = 800;
          if (w > h) {
            if (w > max) {
              h = (h * max) / w;
              w = max;
            }
          } else {
            if (h > max) {
              w = (w * max) / h;
              h = max;
            }
          }
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(resolve, 'image/jpeg', 0.7);
        };
        img.onerror = () => {
          URL.revokeObjectURL(img.src);
          reject(new Error('Format image invalide'));
        };
        img.src = URL.createObjectURL(file);
      });
      const sRef = ref(storage, `chat_images/${userId}/chat_${Date.now()}.jpg`);
      await uploadBytes(sRef, blob);
      const url = await getDownloadURL(sRef);
      return { success: true, url };
    } catch (err) {
      console.error('conversationService.uploadChatImage error:', err);
      return { success: false, error: "Erreur lors de l'envoi de l'image" };
    }
  },

  // ── Utilitaires ─────────────────────────────────────────

  /**
   * Compte les conversations non-lues.
   */
  getUnreadCount(conversations, userId) {
    return conversations.filter((c) => {
      if (!c.lastMessageSenderId || c.lastMessageSenderId === userId) return false;
      const userReadAt = c.readBy?.[userId];
      if (!userReadAt) return true;
      const lastMsgTime = c.lastMessageAt?.toMillis?.() || 0;
      const readTime = userReadAt?.toMillis?.() || 0;
      return lastMsgTime > readTime;
    }).length;
  },
};

export default conversationService;
